import { Icon } from '@iconify/react';
import { CtyButton } from '../button/CtyButton';

export interface ViewTabOption {
  key: string;
  label: string;
  disabled?: boolean;
}

interface ZoomControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  isLoading: boolean;
  filename?: string;
  // Only rendered when provided, so a single-image component shows no nav arrows at all.
  onPrev?: () => void;
  onNext?: () => void;
  canGoPrev?: boolean;
  canGoNext?: boolean;
  // "Component Group" / "Component Name" style segmented toggle, rendered inline in
  // this same toolbar row. Only rendered when provided (backward compatible).
  viewTabs?: ViewTabOption[];
  activeViewTabKey?: string;
  onViewTabChange?: (key: string) => void;
}

export const ZoomControls: React.FC<ZoomControlsProps> = ({
  onZoomIn,
  onZoomOut,
  onReset,
  isLoading,
  onPrev,
  onNext,
  canGoPrev,
  canGoNext,
  viewTabs,
  activeViewTabKey,
  onViewTabChange,
}) => {
  if (isLoading) return null;
  const showNav = !!(onPrev || onNext);
  const showViewTabs = !!viewTabs?.length;
  return (
    <div className="flex flex-nowrap items-center justify-between gap-2 p-2 bg-default-50 border-b border-default-200 min-w-0 w-full">
      <div className="flex items-center gap-2 shrink-0">
        <CtyButton
          onPress={onZoomOut}
          className="p-2 bg-default-100 hover:bg-default-200 rounded-full min-w-0"
          aria-label="Zoom Out"
          title="Zoom Out"
        >
          <Icon icon="mdi:minus" width="20" height="20" className="text-foreground" />
        </CtyButton>
        <CtyButton
          onPress={onZoomIn}
          className="p-2 bg-default-100 hover:bg-default-200 rounded-full min-w-0"
          aria-label="Zoom In"
          title="Zoom In"
        >
          <Icon icon="mdi:plus" width="20" height="20" className="text-foreground" />
        </CtyButton>
        {showNav && (
          <>
            <div className="w-px h-5 bg-default-300 mx-1" />
            <CtyButton
              onPress={onPrev}
              isDisabled={!canGoPrev}
              className="p-2 bg-default-100 hover:bg-default-200 rounded-full min-w-0"
              aria-label="Previous image"
              title="Previous image"
            >
              <Icon icon="mdi:chevron-left" width="20" height="20" className="text-foreground" />
            </CtyButton>
            <CtyButton
              onPress={onNext}
              isDisabled={!canGoNext}
              className="p-2 bg-default-100 hover:bg-default-200 rounded-full min-w-0"
              aria-label="Next image"
              title="Next image"
            >
              <Icon icon="mdi:chevron-right" width="20" height="20" className="text-foreground" />
            </CtyButton>
          </>
        )}
      </div>

      {showViewTabs && (
        <div className="flex items-center border border-default-300 rounded-full min-w-0 overflow-hidden">
          {viewTabs!.map((tab) => {
            const isActive = tab.key === activeViewTabKey;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => !tab.disabled && onViewTabChange?.(tab.key)}
                disabled={tab.disabled}
                aria-pressed={isActive}
                title={tab.label}
                className={`px-3 py-1.5 text-xs font-medium truncate min-w-0 transition-colors ${
                  isActive
                    ? 'bg-primary/10 text-primary'
                    : 'bg-content1 text-default-500 hover:bg-default-100'
                } ${tab.disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      )}

      <CtyButton
        onPress={onReset}
        className="p-2 bg-default-100 hover:bg-default-200 rounded-full min-w-0 shrink-0"
        aria-label="Reset Zoom"
        title="Reset Zoom"
      >
        <Icon icon="system-uicons:reset" width="20" height="20" className="text-foreground" />
      </CtyButton>
    </div>
  );
};
